import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Search, Smartphone } from "lucide-react";
import { toast } from "react-hot-toast";
import { getStudents } from "../../services/students";
import { checkPaymentStatus } from "../../services/fees";
import { initiateStudentEquityPayment } from "../../services/fees";
import { formatKSh } from "../../utils/currency";
import { StudentWithUserInfo } from "../../types/student";

interface PromptPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
  preselectedStudent?: StudentWithUserInfo;
}

const PromptPaymentModal: React.FC<PromptPaymentModalProps> = ({
  isOpen,
  onClose,
  onPaymentSuccess,
  preselectedStudent,
}) => {
  const [step, setStep] = useState<
    "select-student" | "payment-details" | "processing" | "success" | "failed"
  >("select-student");
  const [selectedStudent, setSelectedStudent] =
    useState<StudentWithUserInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [amount, setAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [notes, setNotes] = useState("");

  // Payment state
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusCheckInterval, setStatusCheckInterval] =
    useState<NodeJS.Timeout | null>(null);
  const [redirectTimeout, setRedirectTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [countdown, setCountdown] = useState<number>(5); // Countdown in seconds for redirect
  const [countdownInterval, setCountdownInterval] =
    useState<NodeJS.Timeout | null>(null);

  // Fetch students
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ["all-students"],
    queryFn: () => getStudents({ limit: 100 }),
    enabled: isOpen,
  });

  const students = studentsData?.data || [];

  // Debug step changes
  // Debug step changes
  useEffect(() => {
    console.log("Prompt Payment Step changed to:", step);
  }, [step]);

  // Filter students based on search
  const filteredStudents = students.filter(
    (student) =>
      student.userInfo.firstName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      student.userInfo.lastName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      student.studentId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset modal state when closed
  const resetModal = () => {
    setStep(preselectedStudent ? "payment-details" : "select-student");
    setSelectedStudent(preselectedStudent || null);
    setSearchTerm("");
    setAmount("");
    setPhoneNumber("");
    setNotes("");
    setIsProcessing(false);
    setCountdown(5);
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
    if (redirectTimeout) {
      clearTimeout(redirectTimeout);
      setRedirectTimeout(null);
    }
    if (countdownInterval) {
      clearInterval(countdownInterval);
      setCountdownInterval(null);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetModal();
    } else if (preselectedStudent) {
      // If a student is preselected, set them and skip to payment details
      setSelectedStudent(preselectedStudent);
      setStep("payment-details");
    }
  }, [isOpen, preselectedStudent]);

  const handleStudentSelect = (student: StudentWithUserInfo) => {
    setSelectedStudent(student);
    setAmount(student.fees?.totalBalance?.toString() || "0");
    setPhoneNumber(student.userInfo.profileDetails?.phone || "");
    setStep("payment-details");
  };

  const handleEquityPayment = async () => {
    if (!selectedStudent || !amount || !phoneNumber) {
      toast.error("Please fill in all required fields");
      return;
    }

    const amountNum = parseFloat(amount);
    if (amountNum <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    if (selectedStudent.fees && amountNum > selectedStudent.fees.totalBalance) {
      toast.error("Amount cannot exceed outstanding balance");
      return;
    }

    setIsProcessing(true);
    setStep("processing");

    try {
      console.log(
        "Initiating Equity payment for student:",
        selectedStudent._id
      );
      const response = await initiateStudentEquityPayment({
        studentId: selectedStudent._id,
        amount: amountNum,
        phoneNumber,
      });

      console.log("Equity initiation response:", response);

      // Handle response - backend returns data directly after API interceptor
      const paymentId = response.data?.paymentId || (response as any).paymentId;
      console.log("Extracted paymentId:", paymentId);

      if (paymentId) {
        toast.success(
          "Equity M-Pesa STK Push initiated. Check the phone for prompt."
        );
        // Start polling for payment status
        startStatusCheck(paymentId);
      } else {
        throw new Error("No payment ID returned from server");
      }
    } catch (error: any) {
      console.error("Equity initiation error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to initiate Equity payment"
      );
      setIsProcessing(false);
      setStep("payment-details");
    }
  };

  const startStatusCheck = (paymentIdToCheck: string) => {
    console.log("Starting status check for payment ID:", paymentIdToCheck);

    const interval = setInterval(async () => {
      try {
        console.log("Checking payment status for ID:", paymentIdToCheck);
        const statusResponse = await checkPaymentStatus(paymentIdToCheck);
        console.log("Status check response:", statusResponse);

        // Handle both wrapped response formats
        const paymentData = statusResponse.success
          ? statusResponse.data
          : statusResponse;

        if (paymentData) {
          const status = (paymentData as any).status;
          const verificationStatus = (paymentData as any).verificationStatus;

          console.log("Payment status:", status);
          console.log("Verification status:", verificationStatus);
          console.log("Status type:", typeof status);
          console.log("Status === 'completed':", status === "completed");

          // Check both status and verificationStatus (for Equity payments)
          if (status === "completed" || verificationStatus === "verified") {
            console.log("✅ Payment completed! Showing success state");
            clearInterval(interval);
            setStatusCheckInterval(null);
            setIsProcessing(false);
            setStep("success");
            setCountdown(5); // 5 seconds countdown for redirect

            toast.success("Payment completed successfully!");

            // Start countdown timer (1 second intervals)
            const newCountdownInterval = setInterval(() => {
              setCountdown((prev) => {
                if (prev <= 1) {
                  clearInterval(newCountdownInterval);
                  setCountdownInterval(null);
                  onPaymentSuccess();
                  onClose();
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
            setCountdownInterval(newCountdownInterval);
          } else if (status === "failed") {
            console.log("❌ Payment failed!");
            clearInterval(interval);
            setStatusCheckInterval(null);
            setIsProcessing(false);
            setStep("failed");
            setCountdown(10); // 10 seconds countdown before returning to details

            toast.error("Payment failed. Please try again.");

            // Start countdown timer
            const newCountdownInterval = setInterval(() => {
              setCountdown((prev) => {
                if (prev <= 1) {
                  clearInterval(newCountdownInterval);
                  setCountdownInterval(null);
                  setStep("payment-details");
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
            setCountdownInterval(newCountdownInterval);
          } else {
            console.log(
              `⏳ Payment status is '${status}', will poll again in 5 seconds`
            );
          }
        } else {
          console.error(
            "❌ Invalid response from checkPaymentStatus:",
            statusResponse
          );
        }
      } catch (error) {
        console.error("❌ Error checking payment status:", error);

        // If we get an auth error, the payment might be completed but user needs to refresh
        if (
          (error as any).response?.status === 403 ||
          (error as any).response?.status === 401
        ) {
          console.log("Auth error - payment may be completed, refreshing data");
          clearInterval(interval);
          setStatusCheckInterval(null);
          toast("Payment processed. Refreshing data...");
          setTimeout(() => {
            onPaymentSuccess();
            onClose();
          }, 1500);
        }
      }
    }, 5000); // Check every 5 seconds

    setStatusCheckInterval(interval);
    console.log("Status check interval set:", interval);

    // Stop checking after 5 minutes
    setTimeout(() => {
      console.log("Status check timeout reached, stopping checks");
      clearInterval(interval);
      setStatusCheckInterval(null);
      if (isProcessing) {
        setIsProcessing(false);
        toast.error(
          "Payment timeout. Please check your payment status manually."
        );
        setStep("payment-details");
      }
    }, 300000); // 5 minutes
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {step === "select-student" && "Select Student"}
            {step === "payment-details" && "Prompt Equity Payment"}
            {step === "processing" && "Processing Payment"}
            {step === "success" && "Payment Successful"}
            {step === "failed" && "Payment Failed"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isProcessing || step === "success" || step === "failed"}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Select Student */}
          {step === "select-student" && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by student name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Students List */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {studentsLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  </div>
                ) : filteredStudents.length > 0 ? (
                  filteredStudents.map((student) => (
                    <div
                      key={student._id}
                      onClick={() => handleStudentSelect(student)}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all duration-200"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {student.userInfo.firstName}{" "}
                            {student.userInfo.lastName}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {student.studentId}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-red-600">
                            {formatKSh(student.fees?.totalBalance || 0)}
                          </p>
                          <p className="text-xs text-gray-500">Outstanding</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No students found</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Payment Details */}
          {step === "payment-details" && selectedStudent && (
            <div className="space-y-6">
              {/* Selected Student Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900">
                  {selectedStudent.userInfo.firstName}{" "}
                  {selectedStudent.userInfo.lastName}
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedStudent.studentId}
                </p>
                <p className="text-sm text-gray-600">
                  Outstanding Balance:{" "}
                  <span className="font-semibold text-red-600">
                    {formatKSh(selectedStudent.fees?.totalBalance || 0)}
                  </span>
                </p>
              </div>

              {/* Payment Details Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Amount (KSh)
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="1"
                    max={selectedStudent.fees?.totalBalance || 0}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="254XXXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {!preselectedStudent && (
                  <button
                    onClick={() => setStep("select-student")}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleEquityPayment}
                  className={`${
                    preselectedStudent ? "w-full" : "flex-1"
                  } bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 rounded-lg flex items-center justify-center gap-2 transition-all duration-200`}
                >
                  <Smartphone className="w-5 h-5" />
                  Initiate Equity Payment
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Processing */}
          {step === "processing" && (
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Processing Payment
                </h3>
                <p className="text-gray-600 mt-2">
                  Please check your phone for the Equity payment prompt.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This may take a few moments...
                </p>
              </div>

              {/* Cancel button */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    // Stop status checking
                    if (statusCheckInterval) {
                      clearInterval(statusCheckInterval);
                      setStatusCheckInterval(null);
                    }
                    setIsProcessing(false);
                    setStep("payment-details");
                    toast(
                      "Payment monitoring stopped. You can check the status manually."
                    );
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Stop Monitoring
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === "success" && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <svg
                    className="w-16 h-16 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-green-600 mb-2">
                  Payment Successful!
                </h3>
                <p className="text-gray-600 mb-4">
                  The Equity payment has been completed successfully.
                </p>
                <p className="text-sm text-gray-500">
                  Redirecting to the payments page in {countdown} second
                  {countdown !== 1 ? "s" : ""}...
                </p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Student:</span>
                  <span className="font-medium">
                    {selectedStudent?.userInfo.firstName}{" "}
                    {selectedStudent?.userInfo.lastName}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium text-green-600">
                    {formatKSh(parseFloat(amount) || 0)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (redirectTimeout) {
                      clearTimeout(redirectTimeout);
                      setRedirectTimeout(null);
                    }
                    if (countdownInterval) {
                      clearInterval(countdownInterval);
                      setCountdownInterval(null);
                    }
                    onPaymentSuccess();
                    onClose();
                  }}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Go to Payments Now
                </button>
                <button
                  onClick={() => {
                    if (redirectTimeout) {
                      clearTimeout(redirectTimeout);
                      setRedirectTimeout(null);
                    }
                    if (countdownInterval) {
                      clearInterval(countdownInterval);
                      setCountdownInterval(null);
                    }
                    onClose();
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Failed */}
          {step === "failed" && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-red-100 p-3">
                  <svg
                    className="w-16 h-16 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-red-600 mb-2">
                  Payment Failed
                </h3>
                <p className="text-gray-600 mb-4">
                  The Equity payment could not be completed. This could be due
                  to insufficient funds, network issues, or user cancellation.
                </p>
                <p className="text-sm text-gray-500">
                  Returning to payment details in {countdown} second
                  {countdown !== 1 ? "s" : ""}...
                </p>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Student:</span>
                  <span className="font-medium">
                    {selectedStudent?.userInfo.firstName}{" "}
                    {selectedStudent?.userInfo.lastName}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Attempted Amount:</span>
                  <span className="font-medium text-red-600">
                    {formatKSh(parseFloat(amount) || 0)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (redirectTimeout) {
                      clearTimeout(redirectTimeout);
                      setRedirectTimeout(null);
                    }
                    if (countdownInterval) {
                      clearInterval(countdownInterval);
                      setCountdownInterval(null);
                    }
                    setStep("payment-details");
                  }}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again Now
                </button>
                <button
                  onClick={() => {
                    if (redirectTimeout) {
                      clearTimeout(redirectTimeout);
                      setRedirectTimeout(null);
                    }
                    if (countdownInterval) {
                      clearInterval(countdownInterval);
                      setCountdownInterval(null);
                    }
                    onClose();
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptPaymentModal;
